/**
 * Transformation Utility Functions
 *
 * Pure functions for transformation insight card logic.
 * Extracted from TransformationInsightCard.tsx (Line Count Refactor 2025-11-21)
 */

export interface TransformationInsightCardProps {
  /** Transformation type (e.g., "Tone Shift", "Clarity Boost") */
  transformationType: string;

  /** Original message excerpt (truncated at 200 chars) */
  beforeText: string;

  /** Transformed message excerpt (truncated at 200 chars) */
  afterText: string;

  /** Key insights about transformation (max 3 recommended) */
  insights: string[];

  /** Impact score 0-10 (converted to qualitative label) */
  impactScore: number;

  /** Current subscription tier level (1-3). May be undefined during loading. */
  tierLevel: number | undefined;

  /** Share button handler with analytics tracking */
  onShare?: () => void;

  /** Additional CSS classes */
  className?: string;

  /** Test ID for automated testing */
  testID?: string;
}

/**
 * Smart text truncation at sentence boundaries
 * Prevents mid-word cuts for better readability
 *
 * @param text - Text to truncate
 * @param maxLength - Maximum character length (default: 200)
 * @returns Truncated text with ellipsis if exceeded
 */
export function truncateAtSentence(text: string | undefined | null, maxLength: number = 200): string {
  if (!text) return '';
  if (text.length <= maxLength) return text;

  const truncated = text.substring(0, maxLength);
  const lastPeriod = truncated.lastIndexOf('.');
  const lastQuestion = truncated.lastIndexOf('?');
  const lastExclamation = truncated.lastIndexOf('!');
  const boundary = Math.max(lastPeriod, lastQuestion, lastExclamation);

  return boundary > 0 ? truncated.substring(0, boundary + 1) : truncated + '...';
}

/**
 * Get qualitative impact label from numeric score
 * 0-3: Low Impact | 4-7: Moderate Impact | 8-10: High Impact
 *
 * @param score - Impact score 0-10
 * @returns Qualitative label
 */
export function getImpactLabel(score: number): 'low' | 'moderate' | 'high' {
  if (score <= 3) return 'low';
  if (score <= 7) return 'moderate';
  return 'high';
}

/**
 * Get impact badge styling based on level
 *
 * @param impact - Impact level (low, moderate, high)
 * @returns Tailwind CSS class string for badge
 */
export function getImpactBadgeStyles(impact: 'low' | 'moderate' | 'high'): string {
  const styles = {
    low: 'bg-red-100 border-red-500',
    moderate: 'bg-yellow-100 border-yellow-500',
    high: 'bg-green-100 border-green-500',
  };
  return styles[impact];
}

/**
 * Y2K Phase 3 (P3): Check if transformation is group dynamics type (API-driven, i18n-safe)
 *
 * Uses API-provided `transformationType` field instead of text keyword matching to ensure
 * i18n compatibility across all languages.
 *
 * Triggers multiple bubble tails visual effect (3 SVG tails: left, right, bottom) when:
 * 1. Transformation is group dynamics type (this function returns true)
 * 2. User is Layer 3 (DramaX subscriber) - enforced by component tier check
 *
 * @param transformationType - Transformation type from API analysis (case-insensitive)
 * @returns true if transformation type matches group dynamics categories
 *
 * @example
 * const transformation = {
 *   transformationType: 'group-alignment',
 *   beforeText: '...',
 *   afterText: '...'
 * };
 *
 * isGroupDynamicsTransformation(transformation.transformationType); // true
 * // Shows 3 bubble tails (left, right, bottom) for Layer 3 users
 *
 * @see ADR-009: Y2K Visual Effects - API-Driven Trigger Logic
 */
export function isGroupDynamicsTransformation(transformationType: string | undefined | null): boolean {
  if (!transformationType) return false;

  const groupTypes = [
    'group-alignment',
    'team-cohesion',
    'collaborative-tone',
    'inclusive-language',
    'collective-voice',
  ];
  return groupTypes.includes(transformationType.toLowerCase());
}
